// src/components/profile/MonthCalendar.jsx
import { useRef, useState, useEffect, forwardRef } from 'react'
import { getLogsForMonth, calcMonthStats } from '../../utils/presenca'
import { getHojeBR } from '../../utils/dataBR'
import { shareCardAsImage, downloadCardAsImage } from '../../lib/shareCard'

// ── Ícones ──────────────────────────────────────────────────────────────────

const ShareIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
    <polyline points="16 6 12 2 8 6" />
    <line x1="12" y1="2" x2="12" y2="15" />
  </svg>
)

const DownloadIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

// ── Helpers ──────────────────────────────────────────────────────────────────

const MONTH_NAMES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro']
const DAY_LABELS = ['D','S','T','Q','Q','S','S']

function buildCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay()
  const lastDate = new Date(year, month + 1, 0).getDate()
  const pad = n => String(n).padStart(2, '0')
  const prefix = `${year}-${pad(month + 1)}-`
  const cells = []
  for (let i = 0; i < firstDay; i++) cells.push(null)
  for (let d = 1; d <= lastDate; d++) cells.push(prefix + pad(d))
  return cells
}

function DayCell({ dateStr, log, todayStr }) {
  if (!dateStr) return <div style={{ aspectRatio: '1' }} />

  const isFuture = dateStr > todayStr
  const isToday = dateStr === todayStr
  const hasNote = log?.note && log.note.trim().length > 0
  const hasCheckin = log?.completed
  const dayNum = parseInt(dateStr.slice(8), 10)

  let bg = 'var(--offwhite)'
  let border = '1.5px solid var(--border)'
  let color = 'var(--muted)'
  let fontWeight = 500

  if (isFuture) { bg = 'transparent'; border = '1.5px solid transparent'; color = 'rgba(46,46,46,0.15)' }
  else if (isToday && !hasCheckin) { bg = 'rgba(47,43,95,0.07)'; border = '1.5px solid var(--navy)'; color = 'var(--navy)' }
  else if (hasNote) { bg = '#7b5ea7'; border = '1.5px solid #7b5ea7'; color = 'white'; fontWeight = 600 }
  else if (hasCheckin) { bg = 'rgba(123,94,167,0.15)'; border = '1.5px solid rgba(123,94,167,0.3)'; color = '#7b5ea7' }

  return (
    <div style={{
      aspectRatio: '1',
      borderRadius: 8,
      background: bg,
      border,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 10,
      fontWeight,
      color,
      fontFamily: 'DM Sans, sans-serif',
    }}>
      {dayNum}
    </div>
  )
}

// ── Card off-screen para html2canvas ────────────────────────────────────────

const PresenceShareCard = forwardRef(function PresenceShareCard({ year, month, monthLogs, stats, streak }, ref) {
  const todayStr = getHojeBR()
  const cells = buildCalendarDays(year, month)

  const logMap = {}
  monthLogs.forEach(l => { logMap[l.date] = l })

  const monthLabel = `${MONTH_NAMES[month]} ${year}`

  return (
    <div ref={ref} style={{
      position: 'fixed',
      left: -9999,
      top: 0,
      width: 390,
      height: 693,
      background: 'linear-gradient(160deg, #0d1a2e 0%, #1a2b45 45%, #0f1f35 100%)',
      borderRadius: 24,
      padding: '36px 32px',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: 'DM Sans, sans-serif',
      color: '#fff',
      gap: 0,
    }}>
      {/* Blobs */}
      <div style={{ position:'absolute', width:280, height:280, background:'#7b5ea7', opacity:0.22, borderRadius:'50%', filter:'blur(60px)', top:-80, right:-80, pointerEvents:'none' }} />
      <div style={{ position:'absolute', width:220, height:220, background:'#6fae8f', opacity:0.15, borderRadius:'50%', filter:'blur(60px)', bottom:40, left:-60, pointerEvents:'none' }} />
      <div style={{ position:'absolute', width:160, height:160, background:'#7b5ea7', opacity:0.18, borderRadius:'50%', filter:'blur(50px)', top:'45%', left:'55%', pointerEvents:'none' }} />

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:20, position:'relative', zIndex:1 }}>
        <span style={{ fontFamily:"'Cormorant Garamond', Georgia, serif", fontSize:18, fontWeight:500, color:'#f2b198' }}>Clube do Eu</span>
        <span style={{ marginLeft:'auto', background:'rgba(123,94,167,0.25)', border:'1px solid rgba(123,94,167,0.4)', borderRadius:20, padding:'3px 12px', fontSize:12, color:'rgba(244,242,238,0.7)' }}>{monthLabel}</span>
      </div>

      {/* Headline */}
      <div style={{ fontFamily:"'Cormorant Garamond', Georgia, serif", fontSize:32, fontWeight:400, fontStyle:'italic', color:'rgba(244,242,238,0.92)', lineHeight:1.2, marginBottom:24, position:'relative', zIndex:1 }}>
        Minha presença<br /><strong style={{ fontStyle:'normal', fontWeight:500, color:'#fff' }}>este mês</strong>
      </div>

      {/* Mini calendário */}
      <div style={{ marginBottom:16, position:'relative', zIndex:1 }}>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, marginBottom:4 }}>
          {DAY_LABELS.map((l,i) => (
            <div key={i} style={{ fontSize:9, textAlign:'center', color:'rgba(244,242,238,0.3)', fontWeight:500, textTransform:'uppercase' }}>{l}</div>
          ))}
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3 }}>
          {cells.map((dateStr, i) => {
            if (!dateStr) return <div key={i} style={{ aspectRatio:'1' }} />
            const log = logMap[dateStr]
            const isFuture = dateStr > todayStr
            const hasNote = log?.note && log.note.trim().length > 0
            const hasCheckin = log?.completed
            let bg = 'rgba(255,255,255,0.06)'
            if (isFuture) bg = 'transparent'
            else if (hasNote) bg = '#7b5ea7'
            else if (hasCheckin) bg = 'rgba(123,94,167,0.3)'
            return <div key={i} style={{ aspectRatio:'1', borderRadius:5, background:bg }} />
          })}
        </div>
      </div>

      {/* Legenda */}
      <div style={{ display:'flex', gap:12, marginBottom:20, position:'relative', zIndex:1 }}>
        {[['#7b5ea7','escrita'], ['rgba(123,94,167,0.3)','presença']].map(([c,l]) => (
          <div key={l} style={{ display:'flex', alignItems:'center', gap:5, fontSize:11, color:'rgba(244,242,238,0.35)' }}>
            <div style={{ width:10, height:10, borderRadius:3, background:c }} />
            {l}
          </div>
        ))}
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:20, position:'relative', zIndex:1 }}>
        {[
          [stats.daysPresent, 'dias presentes'],
          [stats.withWriting, 'com escrita'],
          [`${stats.presencePercent}%`, 'do mês'],
        ].map(([val, lbl]) => (
          <div key={lbl} style={{ background:'rgba(123,94,167,0.15)', border:'1px solid rgba(123,94,167,0.3)', borderRadius:12, padding:'12px 8px', textAlign:'center' }}>
            <div style={{ fontFamily:"'Cormorant Garamond', Georgia, serif", fontSize:36, fontWeight:300, color:'#fff', lineHeight:1, letterSpacing:'-0.02em' }}>{val}</div>
            <div style={{ fontSize:10, color:'rgba(244,242,238,0.4)', marginTop:4 }}>{lbl}</div>
          </div>
        ))}
      </div>

      {/* Streak */}
      <div style={{ background:'rgba(123,94,167,0.2)', border:'1px solid rgba(123,94,167,0.4)', borderRadius:12, padding:'14px 16px', display:'flex', alignItems:'center', gap:12, marginBottom:20, position:'relative', zIndex:1 }}>
        <span style={{ fontSize:28 }}>🔥</span>
        <div>
          <div style={{ fontFamily:"'Cormorant Garamond', Georgia, serif", fontSize:28, fontWeight:400, color:'#fff', lineHeight:1 }}>{streak} dias seguidos</div>
          <div style={{ fontSize:11, color:'rgba(244,242,238,0.4)', marginTop:3 }}>sequência atual</div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ marginTop:'auto', position:'relative', zIndex:1 }}>
        <div style={{ fontSize:14, fontWeight:700, color:'#f2b198', letterSpacing:'0.04em' }}>oclubedoeu.com.br</div>
        <div style={{ fontSize:11, color:'rgba(244,242,238,0.3)', marginTop:2 }}>a sua jornada em números</div>
      </div>
    </div>
  )
})

// ── Componente principal ─────────────────────────────────────────────────────

export default function MonthCalendar({ userId, logs, streak }) {
  const today = new Date(getHojeBR() + 'T12:00:00')
  const [year, setYear] = useState(today.getFullYear())
  const [month, setMonth] = useState(today.getMonth())
  const [monthLogs, setMonthLogs] = useState([])
  const [sharing, setSharing] = useState(false)
  const shareCardRef = useRef(null)

  const todayStr = getHojeBR()
  const isCurrentMonth = year === today.getFullYear() && month === today.getMonth()

  useEffect(() => {
    if (isCurrentMonth) {
      const pad = n => String(n).padStart(2, '0')
      const prefix = `${year}-${pad(month + 1)}-`
      setMonthLogs(logs.filter(l => l.date.startsWith(prefix)))
    } else {
      getLogsForMonth(userId, year, month).then(setMonthLogs)
    }
  }, [year, month, logs, userId, isCurrentMonth])

  const logMap = {}
  monthLogs.forEach(l => { logMap[l.date] = l })

  const stats = calcMonthStats(monthLogs, year, month)
  const cells = buildCalendarDays(year, month)

  const goToPrev = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11) }
    else setMonth(m => m - 1)
  }

  const goToNext = () => {
    if (isCurrentMonth) return
    if (month === 11) { setYear(y => y + 1); setMonth(0) }
    else setMonth(m => m + 1)
  }

  const getFilename = () => `presenca-${MONTH_NAMES[month].toLowerCase()}-${year}.png`

  const handleShare = async () => {
    if (!shareCardRef.current || sharing) return
    setSharing(true)
    try {
      await shareCardAsImage(shareCardRef.current, getFilename(), `Minha presença em ${MONTH_NAMES[month]} no Clube do Eu`)
    } finally {
      setSharing(false)
    }
  }

  const handleDownload = async () => {
    if (!shareCardRef.current || sharing) return
    setSharing(true)
    try {
      await downloadCardAsImage(shareCardRef.current, getFilename())
    } finally {
      setSharing(false)
    }
  }

  return (
    <>
      {/* Card off-screen para captura */}
      <PresenceShareCard
        ref={shareCardRef}
        year={year}
        month={month}
        monthLogs={monthLogs}
        stats={stats}
        streak={streak}
      />

      {/* Card visível */}
      <div className="card">
        {/* Navegação de mês */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
          <button
            onClick={goToPrev}
            style={{ width:28, height:28, borderRadius:'50%', background:'rgba(123,94,167,0.1)', border:'none', color:'var(--lavender)', fontSize:16, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}
          >‹</button>
          <span style={{ fontFamily:"'Cormorant Garamond', Georgia, serif", fontSize:20, fontWeight:400, color:'var(--navy)' }}>
            {MONTH_NAMES[month]} {year}
          </span>
          <button
            onClick={goToNext}
            disabled={isCurrentMonth}
            style={{ width:28, height:28, borderRadius:'50%', background:'rgba(123,94,167,0.1)', border:'none', color:'var(--lavender)', fontSize:16, cursor: isCurrentMonth ? 'default' : 'pointer', opacity: isCurrentMonth ? 0.3 : 1, display:'flex', alignItems:'center', justifyContent:'center' }}
          >›</button>
        </div>

        {/* Labels dos dias */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, marginBottom:4 }}>
          {DAY_LABELS.map((l,i) => (
            <div key={i} style={{ fontSize:10, textAlign:'center', color:'var(--muted)', fontWeight:500, fontFamily:'DM Sans, sans-serif' }}>{l}</div>
          ))}
        </div>

        {/* Células do calendário */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3 }}>
          {cells.map((dateStr, i) => (
            <DayCell key={i} dateStr={dateStr} log={logMap[dateStr]} todayStr={todayStr} />
          ))}
        </div>

        {/* Legenda */}
        <div style={{ display:'flex', gap:12, marginTop:10 }}>
          {[['#7b5ea7','check-in + escrita'],['rgba(123,94,167,0.14)','só check-in']].map(([c,l]) => (
            <div key={l} style={{ display:'flex', alignItems:'center', gap:4, fontSize:10, color:'var(--muted)', fontFamily:'DM Sans, sans-serif' }}>
              <div style={{ width:10, height:10, borderRadius:3, background:c, border: c.startsWith('rgba') ? '1px solid rgba(123,94,167,0.3)' : 'none' }} />
              {l}
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8, marginBottom:10 }}>
        {[
          [stats.daysPresent, 'dias presentes'],
          [stats.withWriting, 'com escrita'],
          [streak, 'dias seguidos'],
        ].map(([val, lbl]) => (
          <div key={lbl} className="stat-card">
            <div className="stat-value">{val}</div>
            <div className="stat-label">{lbl}</div>
          </div>
        ))}
      </div>

      {/* Botões de compartilhamento */}
      <div style={{ display:'flex', gap:8 }}>
        <button
          className="btn btn-primary btn-sm"
          onClick={handleShare}
          disabled={sharing}
          style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}
        >
          <ShareIcon />
          {sharing ? 'Gerando...' : 'Compartilhar mês'}
        </button>
        <button
          className="btn btn-secondary btn-sm"
          onClick={handleDownload}
          disabled={sharing}
          title="Salvar imagem"
          style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:4 }}
        >
          <DownloadIcon />
        </button>
      </div>
    </>
  )
}
